/**
 * useSageConversationSettings — loads the per-user tuning for Sage voice mode
 * (silence-before-send duration + mic sensitivity). Cached in localStorage so
 * the conversation overlay can open without a blocking fetch.
 *
 * Sensitivity maps to concrete VAD thresholds:
 *
 *   low:    speaking > 0.015, silence < 0.008   (picks up quiet speech)
 *   medium: speaking > 0.025, silence < 0.015   (default — quiet office)
 *   high:   speaking > 0.050, silence < 0.035   (ignores background noise)
 */
import { authedFetch } from '@/lib/api';
import { useEffect, useState } from 'react'

export interface ConversationSettings {
  silence_ms: number
  sensitivity: 'low' | 'medium' | 'high'
}

const DEFAULT: ConversationSettings = { silence_ms: 1400, sensitivity: 'medium' }
const CACHE_KEY = 'sage_conversation_settings_v1'

let cached: ConversationSettings | null = null
let inflight: Promise<ConversationSettings> | null = null
const listeners = new Set<(cs: ConversationSettings) => void>()

function readCache(): ConversationSettings {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return DEFAULT
    const parsed = JSON.parse(raw) as Partial<ConversationSettings>
    return normalize(parsed)
  } catch {
    return DEFAULT
  }
}

function normalize(cs: Partial<ConversationSettings>): ConversationSettings {
  const silence = typeof cs.silence_ms === 'number' && cs.silence_ms >= 400 && cs.silence_ms <= 3000
    ? cs.silence_ms : DEFAULT.silence_ms
  const sens = cs.sensitivity === 'low' || cs.sensitivity === 'medium' || cs.sensitivity === 'high'
    ? cs.sensitivity : DEFAULT.sensitivity
  return { silence_ms: silence, sensitivity: sens }
}

function notify(cs: ConversationSettings) {
  cached = cs
  localStorage.setItem(CACHE_KEY, JSON.stringify(cs))
  listeners.forEach(fn => fn(cs))
}

async function fetchSettings(): Promise<ConversationSettings> {
  try {
    const res = await authedFetch('/api/v1/sage/conversation-settings', {}, { redirectOnAuthFailure: false })
    if (!res.ok) return DEFAULT
    const raw = await res.json() as Partial<ConversationSettings>
    return normalize(raw)
  } catch {
    return DEFAULT
  }
}

export function useSageConversationSettings(): [ConversationSettings, (cs: ConversationSettings) => Promise<void>] {
  const [cs, setCs] = useState<ConversationSettings>(() => cached || readCache())

  useEffect(() => {
    const listener = (v: ConversationSettings) => setCs(v)
    listeners.add(listener)
    if (cached === null) {
      if (!inflight) inflight = fetchSettings().then(v => { inflight = null; notify(v); return v })
      inflight.then(v => setCs(v))
    }
    return () => { listeners.delete(listener) }
  }, [])

  const update = async (next: ConversationSettings) => {
    const normalized = normalize(next)
    notify(normalized) // optimistic
    try {
      await authedFetch('/api/v1/sage/conversation-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalized),
      }, { redirectOnAuthFailure: false })
    } catch {
      // Save failed — next page load re-fetches.
    }
  }

  return [cs, update]
}

/**
 * Concrete VAD thresholds for a given sensitivity. Kept alongside the hook
 * so the conversation engine imports a single source of truth.
 */
export function sensitivityThresholds(sens: ConversationSettings['sensitivity']): {
  speaking: number
  silence: number
} {
  switch (sens) {
    case 'low':  return { speaking: 0.015, silence: 0.008 }
    case 'high': return { speaking: 0.050, silence: 0.035 }
    default:     return { speaking: 0.025, silence: 0.015 }
  }
}
